import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ConfigContext, UserContext } from '../../../context';
import { Story, StoryStatus, storyApi } from '../../../services';
import {
  COLLAPSE_STORAGE_KEY,
  CardOpenButton,
  CollapseMode,
  IconLabel,
  PublishToggle,
  ReorderArgs,
  STORY_TAB_ADMIN_COOKIE_KEY,
  SortableItem,
  SortableList,
  StoryTabType,
  StoryTags,
  getCssVar,
  getStorySectionByTabType,
  useAppDispatch,
  useAppSelector,
} from '../../../shared';
import { getStories, togglePublishOnStory, updateStoriesPositionOnDrop } from '../../../slices';
import { NoContent } from '../../Error';
import { CreateStoryModal } from '../CreateStoryModal';
import { DeleteStoryModal } from '../DeleteStoryModal';
import { StoryMenuAdmin } from './StoryMenuAdmin';

import classNames from 'classnames';
import classes from './StoryListAdmin.module.scss';

interface StoryListAdminProps {
  storyTabType?: StoryTabType;
}

export const StoryListAdmin: FunctionComponent<StoryListAdminProps> = memo(({ storyTabType }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { storiesExclude = [] } = useContext(ConfigContext).config.elements.storyTab;

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { stories, isFetched, query } = useAppSelector(({ stories }) => stories);

  const [cookies] = useCookies([STORY_TAB_ADMIN_COOKIE_KEY]);

  const [storyUpdatePositionTrigger] = storyApi.endpoints.storyUpdatePosition.useMutation();

  const [deleteStoryId, setDeleteStoryId] = useState<number | null>(null);

  const [updateStoryId, setUpdateStoryId] = useState<number | null>(null);

  const [publishToggling, setPublishToggling] = useState(false);

  const { requestFilter = {} } = { ...cookies[STORY_TAB_ADMIN_COOKIE_KEY] };

  const [collapseMode, setCollapseMode] = useState(
    localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD
  );

  const storiesFiltered = useMemo(
    () => stories.filter(({ id }) => !storiesExclude.includes(id)),
    [stories, storiesExclude]
  );

  const storyIds = useMemo(() => storiesFiltered.map(({ id }) => id), [storiesFiltered]);

  useEffect(() => {
    window.addEventListener('storage', () => {
      setCollapseMode(localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD);
    });
  }, []);

  const isStandard = useMemo(() => collapseMode === CollapseMode.STANDARD, [collapseMode]);

  const isSearching = Boolean(query);

  const filter = useMemo(() => {
    if (!storyTabType) {
      return { ...requestFilter, query };
    }

    return { ...requestFilter, query, section: getStorySectionByTabType(storyTabType) };
  }, [query, requestFilter, storyTabType]);

  useEffect(() => {
    dispatch(getStories({ channelId, filter }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyTabType, query]);

  const navigateToStoryFeedItems = useCallback(
    (id: number) => {
      navigate(`/admin/${storyTabType ?? 'storyTab'}/${id}/items`);
    },
    [navigate, storyTabType]
  );

  const getTitle = useCallback(
    (title: string) => <div className={classes['story-list__item-top-title']}>{title}</div>,
    []
  );

  const getStatus = useCallback(
    (status: StoryStatus, cardsCount: number) => {
      return (
        <div
          className={classNames(classes['story-list__item-top-status'], {
            [classes['story-list__item-top-status--published']]: status === StoryStatus.PUBLISHED,
          })}
        >
          {t(`common.${status.toLowerCase()}`)}, {t('common.cards-count', { count: cardsCount })}
        </div>
      );
    },
    [t]
  );

  const storyStatusChange = useCallback(
    async (storyId: number, isPublished: boolean) => {
      setPublishToggling(true);

      const status = isPublished ? StoryStatus.UNPUBLISHED : StoryStatus.PUBLISHED;

      try {
        await toast.promise(
          dispatch(togglePublishOnStory({ storyId, fields: { status } })).unwrap(),
          {
            pending: t(`publishToggle.${status}-toggling`),
            success: t(`publishToggle.${status}-toggled`),
            error: t(`publishToggle.${status}-error`),
          }
        );
      } catch (_) {
        toast(t(`publishToggle.${status}-error`));
      } finally {
        setPublishToggling(false);
      }
    },
    [dispatch, t]
  );

  const getContent = useCallback(
    (story: Story) => {
      const { id, title, subtitle, status, cardsCount, image, isReadOnly } = story;
      const { url: imageUrl } = { ...image };

      const isPublished = status === StoryStatus.PUBLISHED;

      if (isStandard) {
        return (
          <>
            <div
              className={classes['story-list__item-top']}
              onClick={() => navigateToStoryFeedItems(id)}
            >
              <CardOpenButton label={title} />

              <div className={classes['story-list__item-top-description']}>
                {getStatus(status, cardsCount)}

                <StoryTags story={story} />

                {getTitle(title)}
                {subtitle && (
                  <div className={classes['story-list__item-top-subTitle']}>{subtitle}</div>
                )}
              </div>
              {imageUrl && (
                <div className={classes['story-list__item-top-image-wrapper']}>
                  <img className={classes['story-list__item-top-image']} src={imageUrl} alt={''} />
                </div>
              )}
            </div>
            <div className={classes['story-list__item-bottom']}>
              <PublishToggle
                isPublished={isPublished}
                onClick={() => storyStatusChange(id, isPublished)}
                disabled={publishToggling}
              />
              <IconLabel
                iconId={'edit'}
                iconSize={18}
                label={t('common.edit')}
                color={getCssVar('--base-link-text-color')}
                hoverColor={getCssVar('--base-link-text-hover-color')}
                onClick={() => setUpdateStoryId(id)}
              />
              <IconLabel
                iconId={'delete'}
                iconSize={18}
                label={t('common.delete')}
                color={getCssVar('--color-danger')}
                hoverColor={getCssVar('--color-danger-hover')}
                onClick={() => setDeleteStoryId(id)}
              />
              <StoryMenuAdmin storyId={id} isReadOnly={isReadOnly} />
            </div>
          </>
        );
      }

      return (
        <div
          className={classes['story-list__item-top']}
          onClick={() => navigateToStoryFeedItems(id)}
        >
          <CardOpenButton label={title} />

          <div className={classes['story-list__item-top-description']}>
            {getStatus(status, cardsCount)}
            {getTitle(title)}
          </div>
        </div>
      );
    },
    [
      getStatus,
      getTitle,
      isStandard,
      navigateToStoryFeedItems,
      publishToggling,
      storyStatusChange,
      t,
    ]
  );

  const reorderHandler = useCallback(
    async ({ activeId, overId }: ReorderArgs) => {
      const overStory = storiesFiltered.find(({ id }) => id === overId);

      if (!overStory) {
        return;
      }

      const { position } = overStory;

      dispatch(updateStoriesPositionOnDrop({ draggingStoryId: activeId, targetStoryId: overId }));

      const updated = await storyUpdatePositionTrigger({ storyId: activeId, position }).unwrap();

      if (!updated) {
        dispatch(getStories({ channelId, filter }));
      }
    },
    [channelId, dispatch, filter, storiesFiltered, storyUpdatePositionTrigger]
  );

  if (!isFetched) {
    return (
      <div className={classes['story-list__loader']}>
        <Skeleton height={'5rem'} />
        <Skeleton height={'5rem'} />
        <Skeleton height={'5rem'} />
      </div>
    );
  }

  if (!storiesFiltered.length) {
    return <NoContent />;
  }

  return (
    <div className={classes['story-list']}>
      <SortableList items={storyIds} onReorder={reorderHandler} disabled={isSearching}>
        {storiesFiltered.map((story) => {
          const { id } = story;

          return (
            <SortableItem
              key={id}
              id={id}
              className={classNames(`story-${id}`, classes['story-list__item'])}
            >
              {!isSearching && storiesFiltered.length > 1 && (
                <IconLabel
                  iconId={'drag'}
                  iconSize={14}
                  className={classes['story-list__item-drag-icon']}
                  singleColor
                />
              )}
              <div className={classes['story-list__item-content']}>{getContent(story)}</div>
            </SortableItem>
          );
        })}
      </SortableList>

      {deleteStoryId && (
        <DeleteStoryModal
          isOpen={Boolean(deleteStoryId)}
          onClose={() => setDeleteStoryId(null)}
          storyId={deleteStoryId}
        />
      )}

      {updateStoryId && (
        <CreateStoryModal
          isOpen={Boolean(updateStoryId)}
          onClose={() => setUpdateStoryId(null)}
          story={storiesFiltered.find(({ id }) => id === updateStoryId) as Story}
          storyTabType={storyTabType}
        />
      )}
    </div>
  );
});
